/**
 * EntrepriseLogoField — Batch #14.318 : logo de l'entreprise (le client), imprimé à côté du nom et de l'adresse
 * sur les états financiers et tous les rapports PDF de la tenue de livres (bilan, résultats, grand livre, journaux…).
 */
import { useRef, useState } from "react";
import axios from "axios";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "@/lib/toast";
import { API, authH } from "./_shared";

const btnCls = "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-xs font-semibold disabled:opacity-50";

export default function EntrepriseLogoField({ masterId, logoUrl, onChange }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/admin/entrepreneurs/${masterId}/logo`, fd, { headers: { ...authH(), "Content-Type": "multipart/form-data" } });
      onChange(r.data.logo_url);
      setVer(Date.now());
      toast.success("Logo enregistré — il apparaîtra à côté du nom sur les états financiers et rapports");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("Retirer le logo de l'entreprise des rapports ?")) return;
    setBusy(true);
    try { await axios.delete(`${API}/admin/entrepreneurs/${masterId}/logo`, { headers: authH() }); onChange(""); toast.success("Logo retiré"); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  return (
    <div className="grid sm:grid-cols-[auto_1fr] gap-4 items-start" data-testid="ent-logo-field">
      <div className="w-44 h-20 border-2 border-dashed border-slate-300 rounded-xl bg-white flex items-center justify-center overflow-hidden" data-testid="ent-logo-preview">
        {logoUrl
          ? <img src={`${API}${logoUrl.replace(/^\/api/, "")}?v=${ver}`} alt="Logo de l'entreprise" className="max-w-full max-h-full object-contain p-1" data-testid="ent-logo-img" />
          : <span className="text-[11px] text-slate-400 text-center px-2">Aucun logo<br />(facultatif)</span>}
      </div>
      <div className="space-y-2">
        <p className="text-xs text-slate-600">Logo du client imprimé <strong>à côté du nom et de l'adresse</strong> sur les états financiers et les rapports PDF (bilan, résultats, grand livre, journaux). PNG ou JPG, max 2 Mo — idéalement horizontal sur fond blanc.</p>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="ent-logo-file" />
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className={btnCls} data-testid="ent-logo-upload-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />} {logoUrl ? "Remplacer le logo" : "Téléverser un logo"}
          </button>
          {logoUrl && <button type="button" onClick={remove} disabled={busy} className={btnCls} data-testid="ent-logo-remove-btn"><Trash2 className="w-3.5 h-3.5" /> Retirer</button>}
        </div>
      </div>
    </div>
  );
}
